"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Input = exports.InputConfiguration = exports.InputFooter = void 0;
const react_1 = require("@thesisedu/react");
const icons_1 = require("@thesisedu/react/icons");
const react_2 = __importDefault(require("react"));
const ElementReferenceFields_1 = require("../../editor/ElementReferenceFields");
const log_1 = require("../../log");
const StepNavigation_1 = require("../StepNavigation");
function normalizeString(s) {
    return s.toLowerCase().replace(/[\n !#$%&()*+./=@^_`~-]/g, '');
}
function compareValues(input, expect, partial) {
    if (typeof input === 'boolean') {
        const numExpect = parseFloat(expect);
        const boolExpect = expect.toLowerCase() === 'false' ? false : expect.toLowerCase() === 'true' ? true : null;
        if (!isNaN(numExpect)) {
            return !!input === !!numExpect;
        }
        else if (boolExpect !== null) {
            return !!input === !!boolExpect;
        }
        else {
            return !!input === !!expect;
        }
    }
    else {
        const nInput = normalizeString(input.toString());
        const nExpect = normalizeString(expect.toString());
        (0, log_1.debug)('comparing input strings %s -> %s', nInput, nExpect);
        return partial ? nInput.includes(nExpect) : nInput === nExpect;
    }
}
const inputTags = ['input', 'textarea', 'select'];
function getInputElement(parentElement) {
    const selfIsValid = inputTags.some(tag => parentElement.tagName.toLowerCase() === tag);
    const selectionItem = parentElement.querySelector('.ant-select-selector');
    const child = inputTags.reduce((acc, tag) => {
        if (acc) {
            return acc;
        }
        else {
            const child = parentElement.getElementsByTagName(tag)[0];
            return child || null;
        }
    }, null);
    return selfIsValid ? parentElement : selectionItem || child;
}
function requiresPolling(element) {
    const tag = element.tagName.toLowerCase();
    const isDatePicker = !!element.closest('.rdt');
    return !inputTags.includes(tag) || isDatePicker;
}
function getInputValue(element) {
    var _a;
    const inputType = element.type;
    const tag = element.tagName.toLowerCase();
    if (inputType === 'checkbox' || inputType === 'radio') {
        return element.checked;
    }
    else if (tag === 'select') {
        const select = element;
        return (_a = select.options[select.selectedIndex]) === null || _a === void 0 ? void 0 : _a.value;
    }
    else if (tag === 'span' || tag === 'div') {
        return element.innerText;
    }
    else {
        return element.value;
    }
}
function useWaitForInput(element, expect, onInput, partialMatch) {
    react_2.default.useEffect(() => {
        const htmlElement = document.querySelector(element.selector);
        const input = htmlElement ? getInputElement(htmlElement) : undefined;
        if (input) {
            input.focus();
            let handle = null;
            if (requiresPolling(input)) {
                handle = setInterval(() => {
                    const value = getInputValue(input);
                    onInput(compareValues(value, expect, partialMatch));
                }, 200);
                return () => clearInterval(handle);
            }
            else {
                const onChange = () => {
                    const value = getInputValue(input);
                    clearTimeout(handle);
                    handle = setTimeout(() => {
                        onInput(compareValues(value, expect, partialMatch));
                    }, 200);
                };
                const onBlur = () => {
                    input.focus();
                };
                input.addEventListener('keyup', onChange);
                input.addEventListener('click', onChange);
                input.addEventListener('change', onChange);
                input.addEventListener('blur', onBlur);
                onChange();
                return () => {
                    input.removeEventListener('keyup', onChange);
                    input.removeEventListener('click', onChange);
                    input.removeEventListener('change', onChange);
                    input.removeEventListener('blur', onBlur);
                    input.blur();
                };
            }
        }
    }, [element.selector]);
}
function InputFooter({ step, onUpdateStep }) {
    const [isComplete, setIsComplete] = react_2.default.useState(false);
    const placeholders = (0, react_1.usePlaceholders)();
    useWaitForInput(step.element, (0, react_1.markdownToPlainText)(step.requiredInput, placeholders), setIsComplete, step.partialMatch);
    return (react_2.default.createElement(StepNavigation_1.StepNavigation, { onUpdateStep: onUpdateStep, message: isComplete ? (react_2.default.createElement(react_1.Color, { color: '@green' },
            react_2.default.createElement(react_1.SolidIcon, null,
                react_2.default.createElement(icons_1.Check, null)),
            "\u00A0",
            react_2.default.createElement(react_1.Markdown, { inline: true, children: step.completeMessage }))) : (react_2.default.createElement(react_2.default.Fragment, null,
            react_2.default.createElement(icons_1.InputField, null),
            react_2.default.createElement(react_1.Markdown, { inline: true, children: step.actionMessage }))), disabled: !isComplete }));
}
exports.InputFooter = InputFooter;
function InputConfiguration({ prefix }) {
    const theme = (0, react_1.useTheme)();
    return (react_2.default.createElement(react_2.default.Fragment, null,
        react_2.default.createElement(ElementReferenceFields_1.ElementReferenceFields, { prefix: `${prefix}.element`, required: true }),
        react_2.default.createElement(react_1.Form.Item, { name: `${prefix}.actionMessage`, label: 'Action Message', required: { value: true, message: 'An action message is required.' }, extra: 'Shown in the footer to give the user an exact instruction on what to do. Supports Markdown.' },
            react_2.default.createElement(react_1.Input, { placeholder: 'eg. Type `something` in the field.' })),
        react_2.default.createElement(react_1.Form.Item, { name: `${prefix}.completeMessage`, label: 'Complete Message', required: { value: true, message: 'A complete message is required.' }, extra: 'Shown in the footer to the user once the step is complete. Supports Markdown.' },
            react_2.default.createElement(react_1.Input, { placeholder: 'eg. Good job!' })),
        react_2.default.createElement(react_1.Form.Item, { name: `${prefix}.requiredInput`, label: 'Required Input', required: { value: true, message: 'Required input is required.' }, extra: 'The input the user is required to put into the field in order to continue. Use "true" and "false" for boolean values.' },
            react_2.default.createElement(react_1.Input, { style: { fontFamily: theme['@code-family'] } })),
        react_2.default.createElement(react_1.Form.Item, { name: `${prefix}.partialMatch`, label: 'Partial Match', valuePropName: 'checked' },
            react_2.default.createElement(react_1.Checkbox, null, "Require only a partial match for this step to be complete."))));
}
exports.InputConfiguration = InputConfiguration;
exports.Input = {
    label: 'Input',
    renderConfiguration: InputConfiguration,
    renderFooter: InputFooter,
};
//# sourceMappingURL=Input.js.map